import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Mutable, PropertyValuePair, SelectedMutations } from '@feedyou/utils'
import { AnswerStyle, DialogId, PromptAnswerSchema, PromptStepSchema } from '@feedyou/schema'

import { updateMutableValue } from '../../Step/StepService'
import { sanitizeText } from '../../../lib/sanitizeText'
import { WritableDraft } from 'immer/dist/types/types-external'
import { generateId } from '../../../lib/generateId'

type PromptStepReducerState = Mutable<PromptStepSchema>
type WithMutations<T extends object> = T & { selectedMutations: SelectedMutations | null }

const slice = createSlice({
  name: 'steps/prompt',
  initialState: {} as PromptStepReducerState,
  reducers: {
    setPrompt: (
      step,
      action: PayloadAction<{ prompt: string; selectedMutations: SelectedMutations }>
    ) => {
      const { prompt, selectedMutations } = action.payload

      return updateMutableValue(step, step.type, sanitizeText(prompt), selectedMutations)
    },

    setStorage: (step, action: PayloadAction<string | undefined>) => {
      step.storage = sanitizeText(action.payload)
    },

    setValidDialog: (step, action: PayloadAction<DialogId | undefined>) => {
      if (!step.validator) {
        step.validator = {}
      }

      step.validator.validDialog = action.payload
    },

    setInvalidDialog: (step, action: PayloadAction<DialogId | undefined>) => {
      if (!step.validator) {
        step.validator = {}
      }

      step.validator.invalidDialog = action.payload
    },

    setListStyle: (step, action: PayloadAction<AnswerStyle | undefined>) => {
      step.listStyle = action.payload
    },

    editAnswerProperty: (
      step,
      action: PayloadAction<
        WithMutations<PropertyValuePair<PromptAnswerSchema> & { answerId: string }>
      >
    ) => {
      const { answerId, property, value, selectedMutations } = action.payload

      step.answers = step.answers?.map(answer => {
        const updatedAnswer: WritableDraft<PromptAnswerSchema> =
          answer.id === answerId
            ? updateMutableValue(answer as any, property, value, selectedMutations)
            : answer

        if (updatedAnswer.answer === updatedAnswer.button) {
          // strange that mutations are not taken into account, but it
          delete updatedAnswer['button']
        }

        return updatedAnswer
      })
    },

    deleteAnswer: (step, action: PayloadAction<{ index: number }>) => {
      step.answers = step.answers?.filter((answer, index) => index !== action.payload.index)
    },

    addAnswer: (step, action: PayloadAction<void>) => {
      if (!step.answers) {
        step.answers = []
      }

      step.answers.push({ id: generateId.stepId(), answer: '' })
    },

    clearAllAnswers: step => {
      step.answers = []
      step.defaultAnswer = undefined
    },

    editDefaultAnswerProperty: (
      step,
      action: PayloadAction<WithMutations<PropertyValuePair<PromptAnswerSchema>>>
    ) => {
      step.defaultAnswer = updateMutableValue(
        (step.defaultAnswer as any) || {},
        action.payload.property,
        action.payload.value,
        action.payload.selectedMutations
      )
    },

    deleteDefaultAnswer: (step, action: PayloadAction<void>) => {
      delete step.defaultAnswer
    },

    addDefaultAnswer: (step, action: PayloadAction<void>) => {
      step.defaultAnswer = { id: `${generateId.stepId()}-default` }
    }
  }
})

export const actions = slice.actions
export const reducer = slice.reducer
